'use client';

import {
  AlertTriangle,
  Lightbulb,
  Sparkles,
  Info,
  Zap,
  Target,
  Quote,
} from 'lucide-react';
import type { ReactNode } from 'react';

type CalloutType =
  | 'insight'
  | 'warning'
  | 'tip'
  | 'note'
  | 'example'
  | 'important';

interface CalloutProps {
  type?: CalloutType;
  title?: string;
  children: ReactNode;
}

const calloutConfig: Record<
  CalloutType,
  {
    icon: typeof Lightbulb;
    defaultTitle: string;
    bg: string;
    iconColor: string;
    border: string;
  }
> = {
  insight: {
    icon: Lightbulb,
    defaultTitle: 'Key Insight',
    bg: 'bg-amber-50/80',
    iconColor: 'text-amber-600',
    border: 'border-l-amber-400',
  },
  warning: {
    icon: AlertTriangle,
    defaultTitle: 'Warning',
    bg: 'bg-red-50/80',
    iconColor: 'text-red-500',
    border: 'border-l-red-400',
  },
  tip: {
    icon: Sparkles,
    defaultTitle: 'Pro Tip',
    bg: 'bg-emerald-50/80',
    iconColor: 'text-emerald-600',
    border: 'border-l-emerald-400',
  },
  note: {
    icon: Info,
    defaultTitle: 'Note',
    bg: 'bg-blue-50/80',
    iconColor: 'text-blue-500',
    border: 'border-l-blue-400',
  },
  example: {
    icon: Zap,
    defaultTitle: 'Example',
    bg: 'bg-violet-50/80',
    iconColor: 'text-violet-500',
    border: 'border-l-violet-400',
  },
  important: {
    icon: Target,
    defaultTitle: 'Important',
    bg: 'bg-gray-100/80',
    iconColor: 'text-gray-700',
    border: 'border-l-gray-900',
  },
};

export function Callout({ type = 'note', title, children }: CalloutProps) {
  const config = calloutConfig[type];
  const Icon = config.icon;

  return (
    <aside
      className={`
        relative my-8 rounded-r-lg border-l-4 ${config.border} ${config.bg}
        p-5 pl-6 not-prose
      `}
    >
      <div className="flex items-start gap-4">
        <div className={`shrink-0 mt-0.5 ${config.iconColor}`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm mb-1">
            {title || config.defaultTitle}
          </p>
          <div className="text-gray-700 text-[15px] leading-relaxed [&>p]:m-0 [&>p:not(:last-child)]:mb-2">
            {children}
          </div>
        </div>
      </div>
    </aside>
  );
}

// Convenience exports
export function Insight({ title, children }: Omit<CalloutProps, 'type'>) {
  return (
    <Callout type="insight" title={title}>
      {children}
    </Callout>
  );
}

export function Warning({ title, children }: Omit<CalloutProps, 'type'>) {
  return (
    <Callout type="warning" title={title}>
      {children}
    </Callout>
  );
}

export function Tip({ title, children }: Omit<CalloutProps, 'type'>) {
  return (
    <Callout type="tip" title={title}>
      {children}
    </Callout>
  );
}

export function Note({ title, children }: Omit<CalloutProps, 'type'>) {
  return (
    <Callout type="note" title={title}>
      {children}
    </Callout>
  );
}

export function Example({ title, children }: Omit<CalloutProps, 'type'>) {
  return (
    <Callout type="example" title={title}>
      {children}
    </Callout>
  );
}

export function Important({ title, children }: Omit<CalloutProps, 'type'>) {
  return (
    <Callout type="important" title={title}>
      {children}
    </Callout>
  );
}

// Large pull quote for emphasis
interface PullQuoteProps {
  children: ReactNode;
  author?: string;
  role?: string;
}

export function PullQuote({ children, author, role }: PullQuoteProps) {
  return (
    <figure className="my-12 not-prose">
      <div className="relative">
        <Quote className="absolute -top-2 -left-2 w-10 h-10 text-amber-200 -z-10" />
        <blockquote
          className="text-2xl md:text-3xl font-normal text-gray-900 leading-relaxed pl-6"
          style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
        >
          {children}
        </blockquote>
      </div>
      {(author || role) && (
        <figcaption className="mt-4 pl-6 text-sm text-gray-500">
          {author && <span className="font-medium text-gray-700">{author}</span>}
          {author && role && <span className="mx-2">·</span>}
          {role && <span>{role}</span>}
        </figcaption>
      )}
    </figure>
  );
}
